import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { GoogleMap, Marker } from '@react-google-maps/api';
import styled from 'styled-components';
import * as Sentry from '@sentry/browser';

import { colors } from 'app/variables';
import UnstyledButton from 'app/commonComponents/UnstyledButton';

import { useAppContext } from '../../hooks';

import FormError from '../FormError';

const Container = styled.div`
  border: 1px solid ${colors.silver};
  border-radius: 5px;
  margin: -0.5rem 0 1rem;
  padding: 1.2rem;
`;

const Heading = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`;

const SubHeading = styled.div`
  margin-bottom: 1rem;
`;

const DismissButton = styled(UnstyledButton)`
  color: ${colors.pacificBlue};
  text-decoration: underline;
`;

function AddressConfirmation({ onPlaceDismiss }) {
  const { placesService } = useAppContext();
  const { values, errors } = useFormikContext();
  const [location, setLocation] = useState({ lat: 0, lng: 0 });

  const {
    google_places_id: googlePlacesId,
    facility_name: facilityName,
    street,
    city,
    country,
    zip_code: zipCode,
  } = values;

  const facilityAddress = [street, zipCode, city, country]
    .filter(Boolean)
    .join(', ');

  useEffect(() => {
    if (googlePlacesId) {
      placesService
        .getDetails({
          placeId: googlePlacesId,
          fields: ['geometry'],
        })
        .then((place) => {
          const { lat, lng } = place.geometry.location;
          setLocation({ lat: lat(), lng: lng() });
        })
        .catch((error) => {
          Sentry.captureException(error);
          console.log(error);
        });
    }
  }, [googlePlacesId, placesService]);

  return (
    <Container>
      <div style={{ marginBottom: '1rem' }}>
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: '145px',
            borderRadius: '5px',
          }}
          zoom={15}
          center={{ lat: location.lat, lng: location.lng }}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: true,
            controlSize: 25,
          }}
        >
          <Marker position={{ lat: location.lat, lng: location.lng }} />
        </GoogleMap>
      </div>
      <div>
        <Heading>{facilityName}</Heading>
        <SubHeading>{facilityAddress}</SubHeading>
        <DismissButton type="button" onClick={onPlaceDismiss}>
          {i18n.gettext('Not your restaurant? Try again')}
        </DismissButton>
      </div>
      {errors.google_places_id && (
        <FormError
          style={{ margin: '1rem 0 0 0' }}
          message={errors.google_places_id}
        />
      )}
    </Container>
  );
}

AddressConfirmation.propTypes = {
  onPlaceDismiss: PropTypes.func.isRequired,
};

export default AddressConfirmation;
